import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import translate from "../../../i18n/translate";
import ApiRequest from "../../../actions/ApiRequest";
import Toast from "../../common/Toast";

// Every topup operator in one table: which gateway serves it, the CellTop
// discount, and whether sellers can see it. A change is applied by the
// backend at once, no restart needed.
const OperatorsPage = ({ isDarktheme }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await ApiRequest.post("operators?operation=get");
      if (res?.data?.status === "success") {
        setRows(res.data.operators || []);
        setEdits({});
      } else {
        Toast.fire({ title: res?.data?.reason || "Something Went Wrong!", icon: "error" });
      }
    } catch (err) {
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const edit = (key, field, value) =>
    setEdits((e) => ({ ...e, [key]: { ...(e[key] || {}), [field]: value } }));

  const pending = (row) => {
    const e = edits[row.key] || {};
    const body = {};
    if (e.discount !== undefined && String(e.discount).trim() !== String(row.discount)) body.discount = e.discount;
    if (e.gateway !== undefined && e.gateway !== row.gateway) body.gateway = e.gateway;
    return body;
  };

  const save = async (row, extra = {}) => {
    const body = { key: row.key, ...pending(row), ...extra };
    if (Object.keys(body).length === 1) return;
    setSaving(row.key);
    try {
      const res = await ApiRequest.post("operators?operation=update", body);
      if (res?.data?.status === "success") {
        setRows((rs) => rs.map((r) => (r.key === row.key ? res.data.operator : r)));
        setEdits((e) => ({ ...e, [row.key]: {} }));
        Toast.fire({ title: translate("operatorSaved"), icon: "success" });
      } else {
        Toast.fire({ title: res?.data?.reason || "Something Went Wrong!", icon: "error" });
      }
    } catch (err) {
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setSaving(null);
    }
  };

  const gatewayColor = (g) =>
    ({ Toppinex: "primary", Local: "success", EPRS: "secondary", "Azy API": "default" }[g] ||
      "default");

  return (
    <Box sx={{ padding: 3, color: isDarktheme ? "#fff" : "inherit" }}>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        {translate("Operators")}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.75, marginTop: 0.5, marginBottom: 3, maxWidth: "70ch" }}>
        {translate("operatorsHint")}
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} sx={{ maxWidth: 980 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate("Operator")}</TableCell>
                <TableCell>{translate("Gateway")}</TableCell>
                <TableCell align="right">{translate("Discount")} %</TableCell>
                <TableCell align="center">{translate("Enabled")}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const e = edits[row.key] || {};
                const discount = e.discount !== undefined ? e.discount : row.discount;
                const gateway = e.gateway !== undefined ? e.gateway : row.gateway;
                const dirty = Object.keys(pending(row)).length > 0;
                return (
                  <TableRow key={row.key} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.label}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.6 }}>
                        {row.product} · #{row.product_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.gateway_editable ? (
                        <Select
                          size="small"
                          value={gateway || ""}
                          onChange={(ev) => edit(row.key, "gateway", ev.target.value)}
                          sx={{ minWidth: 140 }}
                        >
                          {row.gateway_options.map((g) => (
                            <MenuItem key={g} value={g}>
                              {g}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Chip size="small" color={gatewayColor(row.gateway)} label={row.gateway} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, max: 100, step: 0.5, style: { textAlign: "right", width: 70 } }}
                        value={discount}
                        onChange={(ev) => edit(row.key, "discount", ev.target.value)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={row.enabled}
                        disabled={saving === row.key}
                        onChange={(ev) => save(row, { enabled: ev.target.checked })}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!dirty || saving === row.key}
                        onClick={() => save(row)}
                      >
                        {saving === row.key ? <CircularProgress size={18} color="inherit" /> : translate("Save")}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps)(OperatorsPage);
